import {
  DefaultValidationObjectForDocument,
  DiscountType,
  DocumentStatus,
  ELEMENTS_LOOKUP,
  IDocumentNotificationSettings,
  IElement,
  IPricingTable,
  LineItem,
  PriceTypeEnum,
  ProductListElementOptions,
  SentMedium,
  UpdateDocument,
  calculator,
  ccRecipient,
  convertObjToSnakeCase,
  loadFonts,
  normalizeRruleForV2Form,
  setFillableFieldsDataIntoDocument,
  stripUnderscoreIds,
  validateDocumentSaveCardDetails,
} from '@gohighlevel/ghl-proposals-common'
import { computed, ref, toRaw } from 'vue'

import { DocumentServices } from '@/service/DocumentService'
import { useAppStore } from '@/store/app'
import { useBuilderStore } from '@/store/builder'
import { useProductListStore } from '@/store/productListStore'
import { getTimeZoneAbbreviation } from '@/util'
import { AxiosError } from 'axios'
import { union } from 'lodash'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useCurrency } from './useCurrency'
import { useGroups } from './useGroups'
import { useLineItems } from './useLineItems'
import { useNotify } from './useNotify'
import { usePostmate } from './usePostmate'

const loading = ref(false)
const loadingCustomValues = ref(false)
const documentMetaInfo = ref({})
const contentLibraryCustomContentCount = ref(0)
export const useDocument = () => {
  const route = useRoute()
  const { t } = useI18n()
  const appStore = useAppStore()
  const builderStore = useBuilderStore()
  const productListStore = useProductListStore()
  const recipients = computed(() => appStore.document.recipients)
  const { getCurrency } = useCurrency()
  const { handleError, handleSuccess } = useNotify()
  const { redirectOnParentRoute } = usePostmate()
  const { setGroupTargetsOnSelection } = useGroups()
  const documentVersion = computed(() => appStore.document?.version ?? 1)
  const validateDocument = () => {
    const errors = DefaultValidationObjectForDocument
    builderStore.validateSectionHeights()
    const productListErrors = productListStore.getProductListErrors()

    if (recipients.value.length > 0) {
      const primaryRecipient = recipients.value.find((el: any) => el.isPrimary === true)
      errors.isRecipientsAdded = primaryRecipient ? true : false
    } else {
      errors.isRecipientsAdded = false
    }

    const settingsErrors = appStore.getSettingsErrors()
    errors.isNotificationSettingsValid = settingsErrors && settingsErrors.length > 0 ? false : true

    const redirectUrlErrors = appStore.getRedirectUrlSettingErrors()
    errors.isRedirectUrlSettingsValid = redirectUrlErrors && redirectUrlErrors.length > 0 ? false : true

    const customValueLinkageErrors = builderStore.getCustomValueLinkageErrors()
    errors.isCustomValueLinkageValid = customValueLinkageErrors && customValueLinkageErrors.length > 0 ? false : true

    // Document name validation - disallow raw double-quote (")
    const docName = (appStore.document?.name ?? '').toString()
    const hasDoubleQuote = docName.includes('"')

    if (appStore.document?.status === DocumentStatus.DRAFT) {
      errors.isDocumentNameValid = !hasDoubleQuote
      errors.isProductListValid = productListErrors && productListErrors.length > 0 ? false : true
    } else {
      // for non-draft documents, don't apply this validation
      errors.isDocumentNameValid = true
      errors.isProductListValid = true
    }

    const imageSizeErrors = builderStore.getImageSizeErrors()
    errors.isImageSizeValid = imageSizeErrors && imageSizeErrors.length > 0 ? false : true

    const saveCardDetailsValidation = validateDocumentSaveCardDetails(
      builderStore.pages as any,
      appStore.paymentIntegrations,
      appStore.document?.paymentLiveMode ?? true,
      recipients.value
    )
    errors.isPaymentProviderConfigured = saveCardDetailsValidation.isPaymentProviderConfigured
    errors.isPaymentProviderSupportedForSaveCard = saveCardDetailsValidation.isPaymentProviderSupportedForSaveCard
    errors.isSaveCardDetailsAssignedToContact = saveCardDetailsValidation.isSaveCardDetailsAssignedToContact
    errors.isSectionHeightValid = !builderStore.sectionHeightValidationError

    return errors
  }

  const isDocumentValid = () => {
    const validations = validateDocument()
    return Object.values(validations).every(val => val === true)
  }

  const loadDocument = async () => {
    try {
      loading.value = true
      const { data } = await DocumentServices.getDocument({
        locationId: route.params.locationId as string,
        id: route.params.documentId as string,
      })
      const pages = setFillableFieldsDataIntoDocument(data.document.pages, data.document.fillableFields, data.document.pricingTables)
      appStore.setDocument({ ...data.document, pages })
      if (data?.links) {
        appStore.setLinkReference(data?.links)
      }
      if (data?.whiteLabelBaseUrl) {
        appStore.setWhiteLabelViewerBaseUrl(data?.whiteLabelBaseUrl)
      }
      if (data?.invoiceWhiteLabelBaseUrl) {
        appStore.setInvoiceWhiteLabelBaseUrl(data?.invoiceWhiteLabelBaseUrl)
      }
      const updatedPages = pages
      if (updatedPages && updatedPages.length) builderStore.setPages(updatedPages)
      else builderStore.addPage()
      builderStore.setStatus(data.document.status)

      const groups = data?.document?.groups && data?.document?.groups.length ? data?.document?.groups : []
      builderStore.setGroups(groups)

      if (data?.paymentInfo) {
        const paymentInfo = data.paymentInfo
        const generateInvoiceOnSigning = paymentInfo?.generateInvoiceOnSigning as boolean
        productListStore.toggleGenerateInvoiceOnSigning(generateInvoiceOnSigning)
        if (paymentInfo?.paymentSettings?.schedule?.rrule) {
          const loadedRrule = paymentInfo.paymentSettings.schedule.rrule
          const rruleToSet = generateInvoiceOnSigning ? normalizeRruleForV2Form(loadedRrule) : loadedRrule
          productListStore.setScheduleForMode(rruleToSet, generateInvoiceOnSigning)
          productListStore.setInvoiceType(paymentInfo?.invoiceType)
        }
        if (paymentInfo?.generatedInvoiceType) {
          productListStore.setGeneratedInvoiceType(paymentInfo?.generatedInvoiceType)
        }
        if (paymentInfo?.invoiceId) {
          productListStore.setInvoiceId(paymentInfo?.invoiceId)
        }
        if (paymentInfo?.scheduleId) {
          productListStore.setScheduleId(paymentInfo?.scheduleId)
        }
        if (paymentInfo?.paymentDepositSettings) {
          productListStore.setPaymentDepositSchedule(paymentInfo?.paymentDepositSettings)
        }
        productListStore.toggleEnableAutoPayment(paymentInfo?.enableAutoPayment ?? false)
      }

      if (data?.notificationSettings) {
        appStore.setGlobalDocumentSettings(data.notificationSettings)
      }

      if (data?.notificationSettings) {
        appStore.setGlobalDocumentSettings(data.notificationSettings)
      }
      const customValueLinkage = data?.document?.customValueLinkage ? data?.document?.customValueLinkage : {}
      builderStore.setCustomValueLinkage(customValueLinkage)

      if (data?.document?.fontsToLoad && data?.document?.fontsToLoad.length > 0 && data?.document?.version > 1) {
        loadFonts(data?.document?.fontsToLoad)
        builderStore.setLoadedFonts(union(builderStore.loadedFonts, data?.document?.fontsToLoad))
      }
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loading.value = false
    }
  }

  // Add new method to only fetch and update pages
  const updatePagesAfterUpload = async () => {
    try {
      const { data } = await DocumentServices.getDocument({
        locationId: route.params.locationId as string,
        id: route.params.documentId as string,
      })

      // Only process and update pages
      const pages = setFillableFieldsDataIntoDocument(data.document.pages, data.document.fillableFields, data.document.pricingTables)

      // Update only the pages in store
      appStore.setDocument({
        ...appStore.document,
        pages,
      })
      builderStore.setPages(pages)
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    }
  }

  const totalDueAmountOfAllProducts = computed(() => {
    return Number(
      builderStore.pages
        .reduce((acc, page) => {
          page.children?.forEach(child => {
            if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
              const { amountDue } = useLineItems(child.id)
              acc += amountDue.value
            }
            if (child.type === ELEMENTS_LOOKUP.ROW) {
              child?.children?.forEach(column => {
                column?.children?.forEach(child => {
                  if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
                    const { amountDue } = useLineItems(child.id)
                    acc += amountDue.value
                  }
                })
              })
            }
          })
          return acc
        }, 0)
        ?.toFixed(2)
    )
  })

  const subTotalAcrossProductTables = computed(() => {
    return Number(
      builderStore.pages
        .reduce((acc, page) => {
          page.children?.forEach(child => {
            if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
              const { subTotal } = useLineItems(child.id)
              acc += subTotal.value
            }
            if (child.type === ELEMENTS_LOOKUP.ROW) {
              child?.children?.forEach(column => {
                column?.children?.forEach(child => {
                  if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
                    const { subTotal } = useLineItems(child.id)
                    acc += subTotal.value
                  }
                })
              })
            }
          })
          return acc
        }, 0)
        ?.toFixed(2)
    )
  })

  const totalDiscountInPercentage = computed(() => {
    // check how many product list has for the document.
    // This is added because of the miscalculation while getting the percentage
    // if it has only one product list and the discount type is in percentage take that value.
    const productLists = builderStore.getAllProductList()
    if (productLists && productLists.length === 1) {
      if (productLists[0].component?.options?.discountType === DiscountType.PERCENTAGE) {
        return productLists[0].component?.options?.discountValue || 0
      }
    }

    // otherwise go ahead with the reduce operation.
    let finalProductListVersion = 1
    const totalDiscountAcrossTables = productLists.reduce((acc, productList) => {
      const { totalDiscount, productListVersion } = useLineItems(productList.id)
      finalProductListVersion = productListVersion.value
      acc += totalDiscount.value
      return acc
    }, 0)
    const { getPercentage } = calculator(Number(subTotalAcrossProductTables.value), finalProductListVersion)
    return getPercentage(totalDiscountAcrossTables, finalProductListVersion)
  })

  const saveDocument = async () => {
    if (!appStore.unsavedChanges) return

    try {
      loading.value = true
      // Force any debounced header/footer propagation to fire synchronously BEFORE we read the
      // pages tree for serialisation. Without this, a user who types in a text block and hits
      // save within the debounce window (3s) captures a state where the edit exists only on the
      // host page — other pages still hold the pre-edit cloned section.
      if (typeof (builderStore as any).flushPendingSectionPropagations === 'function') {
        ;(builderStore as any).flushPendingSectionPropagations()
      }
      const fillableFields = builderStore.getFillableFields()
      const discounts = productListStore.getEachDiscountsFromProductList()

      // if any one recurring product line item found, then the invoice should be recurring invoice
      const invoiceType = productListStore.invoiceType
      const schedule =
        invoiceType === PriceTypeEnum.RECURRING
          ? {
              rrule: productListStore.generateInvoiceOnSigning ? productListStore.scheduleV2 : productListStore.schedule,
            }
          : {}
      const paymentDepositSettings = productListStore.paymentDepositSchedule || {}
      const paymentInfo = {
        invoiceType,
        schedule,
        generateInvoiceOnSigning: productListStore.generateInvoiceOnSigning,
        paymentDepositSettings,
        enableAutoPayment: productListStore.enableAutoPayment,
      }
      const redirectSettings = appStore?.document?.redirectSettings ? appStore?.document?.redirectSettings : {}
      if (redirectSettings?.documentRedirectUrl) {
        redirectSettings.documentRedirectUrl = /^https?:\/\//i.test(redirectSettings.documentRedirectUrl)
          ? `${redirectSettings.documentRedirectUrl}`
          : `https://${redirectSettings.documentRedirectUrl}`
      }

      if (appStore?.document?.notificationSettings?.value) {
        delete appStore?.document?.notificationSettings?.value
      }

      const documentPayload = {
        id: route.params.documentId as string,
        name: appStore.document?.name?.trim(),
        locationId: route.params.locationId as string,
        pages: builderStore.pages,
        groups: builderStore.groups,
        variables: appStore.document?.variables,
        recipients: appStore.document?.recipients,
        timezone: {
          zone: appStore.timeZone,
          abbreviation: getTimeZoneAbbreviation(appStore.timeZone),
        },
        grandTotal: {
          currency: productListStore.currency || getCurrency(),
          amount: totalDueAmountOfAllProducts.value || 0,
          discountPercentage: totalDiscountInPercentage.value,
          discounts,
        },
        fillableFields: fillableFields,
        enableSigningOrder: appStore.document.enableSigningOrder,
        pricingTables: builderStore.pages.reduce((acc, page) => {
          page?.children?.forEach((child: IElement) => {
            if (child.version > 1 && child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
              acc.push({
                version: child.version,
                id: child.id,
                lineItems: (child as IElement<ProductListElementOptions>).component.options.lineItems as LineItem[],
              })
            }
            if (child.type === ELEMENTS_LOOKUP.ROW) {
              child?.children?.forEach(column => {
                column?.children?.forEach(child => {
                  if (child.version > 1 && child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
                    acc.push({
                      version: child.version,
                      id: child.id,
                      lineItems: (child as IElement<ProductListElementOptions>).component.options.lineItems as LineItem[],
                    })
                  }
                })
              })
            }
          })
          return acc
        }, [] as IPricingTable[]),
        paymentInfo,
        enableAutoSendInvoice: appStore.document.enableAutoSendInvoice,
        enableDirectPayment: appStore.document.enableDirectPayment,
        paymentLiveMode: appStore?.document?.paymentLiveMode ?? true,
        customValueLinkage: builderStore.customValueLinkage,
        ccRecipients: appStore.document?.ccRecipients?.map(({ _id, ...rest }) => rest) || [],
        notificationSettings: appStore?.document?.notificationSettings ?? {},
        redirectSettings: redirectSettings,
        fontsToLoad: builderStore.fontsToLoad,
        emailAttachments: appStore.document?.emailAttachments || [],
      } as unknown as UpdateDocument

      if (appStore.document?.expiresOn) {
        documentPayload.expiresOn = appStore.document.expiresOn
      }

      if (appStore.document?.expiryDays) {
        documentPayload.expiryDays = appStore.document.expiryDays
      }

      documentPayload.isExpiryEnabled = !!appStore.document.isExpiryEnabled

      documentPayload.isExpiryReminderEnabled = !!appStore.document.isExpiryReminderEnabled

      const { data } = await DocumentServices.updateDocument(documentPayload)
      appStore.setDocument(data.document)
      handleSuccess(t('common.documentSaved'))
    } catch (error) {
      handleError(error as AxiosError)
    } finally {
      loading.value = false
      appStore.setUnsavedChanges(false)
    }
  }

  const sendDocument = async (
    medium: SentMedium,
    params?: {
      name?: string
      ccRecipients: ccRecipient[]
      notificationSettings?: IDocumentNotificationSettings
    }
  ) => {
    try {
      loading.value = true
      const body = {
        documentId: route.params.documentId as string,
        locationId: route.params.locationId as string,
        medium,
        ...(params?.name && { documentName: params.name }),
        ...(params?.ccRecipients && { ccRecipients: params.ccRecipients }),
        ...(params?.notificationSettings && {
          notificationSettings: stripUnderscoreIds(toRaw(params.notificationSettings), ['value']),
        }),
      }

      const { data } = await DocumentServices.sendDocument(body)
      if (data?.links) {
        appStore.setLinkReference(data?.links)
      }
      builderStore.updateActiveElement(null)
      setGroupTargetsOnSelection([])

      //In case document is sent rather than resent, then update the status to sent
      if (
        appStore.document.status !== DocumentStatus.DECLINED &&
        appStore.document.status !== DocumentStatus.COMPLETED &&
        appStore.document.status !== DocumentStatus.VIEWED
      ) {
        appStore.document.status = DocumentStatus.SENT
        builderStore.setStatus(DocumentStatus.SENT)
      }
      const message = medium === SentMedium.LINK ? t('common.linkGeneratedSuccessfully') : t('common.documentSentSuccessfully')
      handleSuccess(message)
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loading.value = false
    }
  }

  const updateDocumentStatus = async (status: DocumentStatus, params?: any) => {
    try {
      loading.value = true
      const { data } = await DocumentServices.updateDocumentStatus({
        documentId: route.params.documentId as string,
        status,
        ...params,
      })
      return data
    } catch (err) {
      console.error(err)
      handleError(err as AxiosError)
      throw err
    } finally {
      loading.value = false
    }
  }

  const convertToDraft = () => {
    return updateDocumentStatus(DocumentStatus.DRAFT).then(async () => {
      await loadDocument()
      builderStore.updateActiveElement(null)
      setGroupTargetsOnSelection([])
      appStore.document.status = DocumentStatus.DRAFT
      builderStore.setStatus(DocumentStatus.DRAFT)
      await fetchAllCustomValues()
    })
  }

  const markAsAccepted = () => {
    return updateDocumentStatus(DocumentStatus.ACCEPTED).then(() => {
      appStore.document.status = DocumentStatus.ACCEPTED
      builderStore.setStatus(DocumentStatus.ACCEPTED)
    })
  }

  const markAsCompleted = () => {
    return updateDocumentStatus(DocumentStatus.COMPLETED).then(async () => {
      await loadDocument()
      builderStore.updateActiveElement(null)
      setGroupTargetsOnSelection([])
      appStore.document.status = DocumentStatus.COMPLETED
      builderStore.setStatus(DocumentStatus.COMPLETED)
      await fetchAllCustomValues()
    })
  }

  const markAsDeclined = async (reasons: string[], reasonBody: string) => {
    return updateDocumentStatus(DocumentStatus.DECLINED, {
      declinedReason: reasons,
      declinedReasonBody: reasonBody,
    }).then(async () => {
      await loadDocument()
      builderStore.updateActiveElement(null)
      setGroupTargetsOnSelection([])
    })
  }
  const removeDocument = async () => {
    try {
      loading.value = true
      const { data } = await DocumentServices.removeDocument(route.params.documentId as string)
      return data?.document
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loading.value = false
    }
  }

  const downloadPdf = async () => {
    try {
      loading.value = true
      const { data } = await DocumentServices.downloadPdf({
        documentId: route.params.documentId as string,
        altType: 'location',
        altId: route.params.locationId as string,
      })
      return data
    } catch (err) {
      console.error(err)
      handleError(err as AxiosError)
    } finally {
      loading.value = false
    }
  }

  function redirectToBuilder(id: string) {
    redirectOnParentRoute({
      name: 'builder',
      params: { documentId: id, locationId: route.params.locationId as string },
    })
  }

  const pushToListPage = () => {
    redirectOnParentRoute({
      name: 'proposals-estimates',
      params: {
        locationId: route.params.locationId as string,
      },
    })
  }

  const redirectToSmsTemplates = () => {
    redirectOnParentRoute(
      {
        name: 'marketing-sms-templates',
        params: { locationId: route.params.locationId as string },
      },
      { newTab: true }
    )
  }

  const saveAsTemplate = async () => {
    try {
      loading.value = true
      const { data } = await DocumentServices.saveAsTemplate({
        locationId: route.params.locationId as string,
        documentId: route.params.documentId as string,
      })
      redirectOnParentRoute({
        name: 'proposals-estimates-templates',
        params: {
          id: data.id,
          locationId: route.params.locationId as string,
        },
      })
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loading.value = false
      appStore.setUnsavedChanges(false)
    }
  }

  const fetchAllCustomValues = async () => {
    const primaryContact = appStore.primaryContact
    try {
      loadingCustomValues.value = true
      const { data } = await DocumentServices.getAllCustomVariables(
        route.params.locationId as string,
        route.params.documentId as string,
        primaryContact?.id
      )
      builderStore.setFlatCustomValues(convertObjToSnakeCase(data, ['document']))
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loadingCustomValues.value = false
    }
  }

  const getDocumentMeta = async () => {
    try {
      loading.value = true
      const { data } = await DocumentServices.getDocumentMetaInfo({
        documentId: route.params.documentId as string,
        locationId: route.params.locationId as string,
      })
      if (data) documentMetaInfo.value = data
      return data
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    } finally {
      loading.value = false
    }
  }

  const getContentLibraryCustomContentCount = async () => {
    try {
      const { data } = await DocumentServices.getContentLibraryCustomContentCount({
        locationId: route.params.locationId as string,
      })
      contentLibraryCustomContentCount.value = data.customCount || 0
      return data
    } catch (error) {
      console.error(error)
      handleError(error as AxiosError)
    }
  }

  return {
    loading,
    saveDocument,
    validateDocument,
    convertToDraft,
    markAsAccepted,
    markAsCompleted,
    markAsDeclined,
    loadDocument,
    updatePagesAfterUpload,
    removeDocument,
    pushToListPage,
    redirectToBuilder,
    redirectToSmsTemplates,
    downloadPdf,
    saveAsTemplate,
    sendDocument,
    isDocumentValid,
    fetchAllCustomValues,
    loadingCustomValues,
    getDocumentMeta,
    documentMetaInfo,
    totalDueAmountOfAllProducts,
    documentVersion,
    getContentLibraryCustomContentCount,
    contentLibraryCustomContentCount,
  }
}
